import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../config";
import Backdrop from "../../Catalogs/Dialog/Backdrop";
import Spin from "../../Spinner/Spinner";
import styles from "./CreateCartDialog.module.css";

const LIMIT = 5;

export const TransferCartDialog = ({
  show = false,
  onClose = () => {},
  onSave = () => {},
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    limit: LIMIT,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Reset dialog state when it is closed
  useEffect(() => {
    if (!show) {
      setSearch("");
      setDebouncedSearch("");
      setUsers([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalUsers: 0,
        limit: LIMIT,
      });
      setPage(1);
      setLoading(false);
      setSelectedUserId(null);
    }
  }, [show]);

  // Debounce search input
  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search, show]);

  // Fetch users when dialog is visible, page changes, or debounced search changes
  useEffect(() => {
    if (!show) return;
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/Admin/users`, {
          params: {
            page,
            limit: LIMIT,
            search: debouncedSearch || undefined,
          },
        });
        setUsers(response.data.users || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
        toast.error("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, debouncedSearch, show]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    if (!selectedUserId) {
      toast.error("Please select a user to transfer the cart");
      return;
    }
    onSave(selectedUserId);
  };

  if (!show) return null;

  return (
    <div>
      <Backdrop show={show} switch={handleClose} />
      <div className={styles.popup}>
        <div className={styles.header}>
          <h2 className={styles.title}>Transfer Cart</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.column}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className={styles.listContainer}>
              {loading ? (
                <div className={styles.spinnerWrapper}>
                  <Spin />
                </div>
              ) : users.length ? (
                <>
                  {users.map((user) => (
                    <button
                      key={user._id}
                      type="button"
                      className={styles.productRow}
                      onClick={() => setSelectedUserId(user._id)}
                    >
                      <div className={styles.productInfo}>
                        <div className={styles.productName}>
                          {user?.name || "-"}
                        </div>
                        <div className={styles.productMeta}>
                          {user?.companyName || "-"}
                        </div>
                        <div className={styles.productMeta}>
                          {user?.email || "-"}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedUserId === user._id}
                        onChange={() => setSelectedUserId(user._id)}
                        style={{ width: 18, height: 18 }}
                      />
                    </button>
                  ))}
                  {pagination.totalPages > 1 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "8px",
                        gap: "8px",
                      }}
                    >
                      <button
                        type="button"
                        className={styles.cancelButton}
                        disabled={page <= 1}
                        onClick={() => page > 1 && setPage(page - 1)}
                      >
                        Previous
                      </button>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#444",
                          fontFamily: "Nunito, sans-serif",
                        }}
                      >
                        Page {pagination.currentPage || page} of{" "}
                        {pagination.totalPages}
                      </span>
                      <button
                        type="button"
                        className={styles.cancelButton}
                        disabled={page >= pagination.totalPages}
                        onClick={() =>
                          page < pagination.totalPages && setPage(page + 1)
                        }
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}>No users found</div>
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.confirmButton}
              onClick={handleSave}
              disabled={loading}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
